<template>
  <div class="add-viewing-appointments-form">
    <div
      v-for="(row, index) in rows"
      :key="index"
      class="row align-items-end mb-2">
      <div class="col-12 col-md-4 mb-2">
        <coozzy-form-input
          v-model="row.date"
          type="date"
          :name="$tc('message.marketingObjects.viewingAppointments.publicForm.start')"
          :state="rowErrors[index].date === false ? false : null"
          @input="onRowChanged(index)" />
      </div>
      <div class="col-6 col-md-3 mb-2">
        <label>{{ $t('message.marketingObjects.viewingAppointments.from') }}*</label>
        <coozzy-form-select
          v-model="row.from"
          :state="rowErrors[index].from === false ? false : null"
          @input="onRowChanged(index)">
          <option value="">
            -
          </option>
          <option
            v-for="t in timeList"
            :key="'from-' + index + '-' + t"
            :value="t">
            {{ t }}
          </option>
        </coozzy-form-select>
      </div>
      <div class="col-5 col-md-3 mb-2">
        <label>{{ $t('message.marketingObjects.viewingAppointments.to') }}*</label>
        <coozzy-form-select
          v-model="row.to"
          :state="rowErrors[index].to === false ? false : null"
          @input="onRowChanged(index)">
          <option value="">
            -
          </option>
          <option
            v-for="t in timeList"
            :key="'to-' + index + '-' + t"
            :value="t">
            {{ t }}
          </option>
        </coozzy-form-select>
      </div>
      <div class="col-1 col-md-2 mb-2 d-flex align-items-center justify-content-end pt-md-4">
        <coozzy-button
          v-if="rows.length > 1"
          design="transparent"
          class="p-0"
          @click="removeRow(index)">
          <coozzy-delete-icon />
        </coozzy-button>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-12 col-md-auto">
        <coozzy-button
          design="default"
          @click="addRow">
          + {{ $t('message.marketingObjects.viewingAppointments.publicForm.addViewingAppointment') }}
        </coozzy-button>
      </div>
    </div>
    <div class="row">
      <div class="col-12 text-right">
        <coozzy-button
          design="primary"
          :disabled="saving"
          @click="save">
          {{ $t('message.generic.form.send') }}
        </coozzy-button>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon'
import CalendarSchedulerApi from '@/misc/apis/CalendarSchedulerApi'
import { dateUtils } from '@/mixins/dateUtils'
import {
  buildQuarterHourTimeList,
  isRowStartInPast,
  padTimePart,
  parseTime,
  rowsOverlap,
  timeToMinutes
} from '@/misc/utils/viewingAppointmentSlotUtils'

function createEmptyRow() {
  return {
    date: '',
    from: '',
    to: ''
  }
}

export default {
  name: 'AddViewingAppointmentsForm',
  components: {
    CoozzyButton,
    CoozzyFormInput,
    CoozzyFormSelect,
    CoozzyDeleteIcon
  },
  mixins: [dateUtils],
  props: {
    token: {
      type: String,
      required: true
    },
    existingAppointments: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      rows: [createEmptyRow()],
      rowErrors: [{ date: null, from: null, to: null }],
      timeList: [],
      saving: false
    }
  },
  mounted() {
    this.timeList = buildQuarterHourTimeList()
  },
  methods: {
    addRow() {
      this.rows.push(createEmptyRow())
      this.rowErrors.push({ date: null, from: null, to: null })
    },
    removeRow(index) {
      if (this.rows.length <= 1) {
        return
      }
      this.rows.splice(index, 1)
      this.rowErrors.splice(index, 1)
    },
    onRowChanged(index) {
      if (this.rowErrors[index]) {
        this.$set(this.rowErrors[index], 'date', null)
        this.$set(this.rowErrors[index], 'from', null)
        this.$set(this.rowErrors[index], 'to', null)
      }
    },
    appointmentToComparable(appointment) {
      if (!appointment?.date || !appointment?.startTime || !appointment?.endTime) {
        return null
      }
      return {
        date: this.objectToIsoDate(appointment.date),
        from: padTimePart(appointment.startTime.hours || 0) + ':' + padTimePart(appointment.startTime.minutes || 0),
        to: padTimePart(appointment.endTime.hours || 0) + ':' + padTimePart(appointment.endTime.minutes || 0)
      }
    },
    validate() {
      let valid = true
      let hasPast = false
      let hasOverlap = false
      let hasStartAfterEnd = false
      this.rowErrors = this.rows.map(() => ({ date: null, from: null, to: null }))

      this.rows.forEach((row, index) => {
        if (!row.date) {
          this.rowErrors[index].date = false
          valid = false
        }
        if (!row.from) {
          this.rowErrors[index].from = false
          valid = false
        }
        if (!row.to) {
          this.rowErrors[index].to = false
          valid = false
        }
        const fromMin = timeToMinutes(row.from)
        const toMin = timeToMinutes(row.to)
        if (fromMin !== null && toMin !== null && fromMin >= toMin) {
          this.rowErrors[index].from = false
          this.rowErrors[index].to = false
          hasStartAfterEnd = true
          valid = false
        }
        if (isRowStartInPast(row)) {
          this.rowErrors[index].date = false
          this.rowErrors[index].from = false
          hasPast = true
          valid = false
        }
      })

      for (let i = 0; i < this.rows.length; i++) {
        for (let j = i + 1; j < this.rows.length; j++) {
          if (rowsOverlap(this.rows[i], this.rows[j])) {
            hasOverlap = true
            valid = false
          }
        }
      }

      const existingComparables = this.existingAppointments
        .map(appointment => this.appointmentToComparable(appointment))
        .filter(Boolean)
      this.rows.forEach(row => {
        existingComparables.forEach(existing => {
          if (rowsOverlap(row, existing)) {
            hasOverlap = true
            valid = false
          }
        })
      })

      if (hasPast) {
        Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.errors.pastStart'), { type: 'error' })
      }
      if (hasStartAfterEnd) {
        Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.errors.startAfterEnd'), { type: 'error' })
      }
      if (hasOverlap) {
        Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.errors.overlap'), { type: 'error' })
      }
      return valid
    },
    buildSlots() {
      return this.rows.map(row => ({
        date: this.toObject(row.date),
        startTime: parseTime(row.from),
        endTime: parseTime(row.to)
      }))
    },
    save() {
      if (!this.validate()) {
        return
      }
      this.saving = true
      CalendarSchedulerApi.submitRequestedAppointments(this.token, this.buildSlots())
        .then(response => {
          this.saving = false
          this.rows = [createEmptyRow()]
          this.rowErrors = [{ date: null, from: null, to: null }]
          Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.publicForm.submittedToast'), { type: 'success' })
          this.$emit('saved', response.appointments || [])
        })
        .catch(e => {
          console.log(e)
          this.saving = false
          Vue.toasted.show(this.$t('message.savingErrors.general'), { type: 'error' })
        })
    }
  }
}
</script>
